import { getOpportunities, deleteOpportunity as deleteOpportunityApi } from '../services/api';
import { createEntityStore } from '@redpanda/utils';
import { notifyDataChanged } from '../utils/dataEvents';
import { toast } from 'react-hot-toast';
import type { CreatorInfo } from '../utils/permissions';
import { entityStoreMessages, removeItemsWhere } from './storeHelpers';

export interface OpportunityStage {
  id: string;
  name: string;
  orderIndex: number;
  probability: number;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    opportunities: number;
  };
}

interface Customer {
  id: string;
  name: string;
  email?: string;
  company?: string;
  archived?: boolean;
}

export interface Opportunity extends CreatorInfo {
  id: string;
  customerId: string;
  title: string;
  description?: string;
  value: number;
  currentStageId: string;
  organizationId: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
  closedAt?: string;
  customer: Customer;
  currentStage: OpportunityStage;
}

const useOpportunityStoreBase = createEntityStore<Opportunity>({
  entityName: 'opportunity',
  fetchFn: getOpportunities,
  deleteFn: deleteOpportunityApi,
  toast,
  messages: entityStoreMessages,
});

export { useOpportunityStoreBase };

export const useOpportunityStore = () => {
  const store = useOpportunityStoreBase();

  const confirmDelete = async () => {
    await store.confirmDelete();
    notifyDataChanged('opportunities');
  };

  return {
    opportunities: store.items,
    pagination: store.pagination,
    error: store.error,
    isPageLoading: store.isPageLoading,
    showDeleteConfirmModal: store.showDeleteConfirmModal,
    isDeleting: store.isDeleting,
    itemToDeleteId: store.itemToDeleteId,
    setCurrentPage: store.setCurrentPage,
    handleDelete: store.handleDelete,
    confirmDelete,
    closeDeleteModal: store.closeDeleteModal,
    fetchOpportunities: store.fetchItems,
    addOpportunity: store.addItem,
    updateOpportunity: store.updateItem,
    replaceOpportunity: (tempId: string, realOpportunity: Opportunity) => store.updateItem(tempId, realOpportunity),
    removeOpportunity: (id: string) => removeItemsWhere<Opportunity>(useOpportunityStoreBase, opportunity => opportunity.id === id),
  };
};
